// FIX-CCS-OUTBOX (CCS Offline Part D, spec §7.1) — the shape of one queued photo.
//
// One item per photo, keyed by the photo id created at pick. The item holds everything the
// upload needs EXCEPT the session token, which is read fresh at send time (never stored).
// The schema is versioned: a future change bumps OUTBOX_ITEM_SCHEMA and migrates in onupgradeneeded.

import type { UploadTransform } from '@/features/capture/lib/originalUpload';

export const OUTBOX_ITEM_SCHEMA = 1 as const;

/** pending = waiting to send · sending = a runner holds it now · failed = refused by the server (Retry) · expired = past R1, to be announced and removed */
export type OutboxState = 'pending' | 'sending' | 'failed' | 'expired';

/** The GPS fix taken at pick (R4). Never replaced at sync. */
export interface OutboxGpsFix {
  lat: number;
  lng: number;
  accuracy_m: number | null;
  /** ISO — when the device took the fix (Geolocation position.timestamp). */
  fix_at: string;
  /** Seconds between the fix and the pick (the fix's age at capture). */
  age_s_at_pick: number;
}

/** What the phone knew at pick (the Part C claims), frozen into the item. */
export interface OutboxClaims {
  picked_at_client: string;
  captured_at_client: string | null;
  offline_at_pick: boolean;
  client_offset_before_s: number | null;
  client_install_id: string;
  /** Assigned at save (not at send) so a later live photo can never overtake an older queued one in sequence. */
  client_seq: number;
}

/** Farm / cycle / crop / stage as known at pick, from the phone's own cache (never a network wait). */
export interface OutboxContext {
  farm_id: string | null;
  cycle_id: string | null;
  crop_id: string | null;
  stage_id: string | null;
}

export type OutboxLastError =
  | 'network'
  | 'timeout'
  | 'session_invalid'
  | 'offline_disabled'
  | 'server_error'
  | 'refused'
  | 'row_not_confirmed';

export interface OutboxItem {
  schema: typeof OUTBOX_ITEM_SCHEMA;
  photo_id: string;
  /** TR-OFF-9: the farmer who took it. Sent only under this farmer's session. */
  owner_user_id: string;
  state: OutboxState;

  original: Blob;
  /** The real type read from the first bytes (A1); the upload file name uses it. */
  original_ext: 'jpg' | 'png' | 'webp';
  preview: Blob | null;
  upload_transform: UploadTransform;
  size_bytes: number;
  /** R7: SHA-256 (hex) of the original bytes. */
  sha256: string;

  category: string;
  context: OutboxContext;
  /** 'capture' when at least the cycle was known at pick (closes SD-423); 'send' otherwise (the server fills it). */
  context_as_of: 'capture' | 'send';
  /** Items saved before FIX-CCS-G-GPS only: the loose pick-time fix. New items carry location_bundle instead. */
  gps: OutboxGpsFix | null;
  /**
   * FIX-CCS-G-GPS (GPS-3 S2 §2.6): the Location Bundle v1, written ONCE — at the shutter, or once when a hold
   * resolves — then frozen; the sync runner sends it verbatim and never recomputes it. null = no evidence fix.
   * Absent on items saved by older builds.
   */
  location_bundle?: Record<string, unknown> | null;
  /** 'holding' = the engine is still waiting for a post-shutter fix; the item is not sent until 'final'. Absent = final. */
  location_state?: 'holding' | 'final';
  claims: OutboxClaims;
  /** Pre-capture record: the app/device/page stamp written at enqueue (SD-O50), plus the background enrichment merged on top when it arrives. */
  precapture: unknown | null;

  /** ISO — written to the outbox. */
  queued_at: string;
  /** The immediate attempt at save has been made (anything sent after that goes with upload_source 'queued'). */
  save_attempt_done: boolean;
  /** Any send attempt for this photo failed → offline_evidence true on every later send. */
  send_failed_before: boolean;
  attempts: number;
  last_error: OutboxLastError | null;
  /** Server error code or HTTP status, for the failed state and the report. */
  last_error_detail: string | null;
  last_attempt_at: string | null;
  /** ISO — back-off: not before this time (network failures only). */
  next_attempt_at: string | null;
}

/** What the farmer's screens need, derived from the store for the signed-in farmer only. */
export interface OutboxSummary {
  /** Mine, waiting (pending + sending). */
  pending: number;
  /** Mine, refused by the server — shown with Retry. */
  failed: number;
  /** Items held for other accounts on this phone (count only; never shown). */
  otherAccounts: number;
  /** Mine, total bytes (for R2). */
  bytes: number;
  /** All owners — R2 counts the whole phone's queue. */
  totalItems: number;
  totalBytes: number;
}
